import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { RsIcon } from '../components/icons'

const ConfirmContext = createContext(null)

const VARIANT_META = {
  danger: { icon: 'alert', iconClass: 'danger' },
  warning: { icon: 'alertTriangle', iconClass: 'warning' },
  default: { icon: 'info', iconClass: 'default' },
}

function normalizeOptions(input) {
  if (typeof input === 'string') {
    return { message: input, variant: 'danger' }
  }
  return input || {}
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null)
  const resolveRef = useRef(null)

  const confirm = useCallback((options) => {
    const opts = normalizeOptions(options)
    return new Promise((resolve) => {
      resolveRef.current = resolve
      setState({
        title: opts.title ?? 'Confirm action',
        message: opts.message ?? '',
        confirmText: opts.confirmText ?? opts.confirmLabel ?? 'Confirm',
        cancelText: opts.cancelText ?? opts.cancelLabel ?? 'Cancel',
        variant: opts.variant ?? 'danger',
      })
    })
  }, [])

  const finish = useCallback((result) => {
    setState(null)
    const resolve = resolveRef.current
    resolveRef.current = null
    resolve?.(result)
  }, [])

  const meta = state ? (VARIANT_META[state.variant] || VARIANT_META.danger) : null

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && createPortal(
        <div
          className="admin-modal-overlay show rs-confirm-overlay"
          onClick={() => finish(false)}
          role="presentation"
        >
          <div
            className="admin-modal admin-modal-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="rs-confirm-title"
            aria-describedby="rs-confirm-message"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-confirm-body">
              <div className={`admin-modal-confirm-icon ${meta.iconClass}`}>
                <RsIcon name={meta.icon} size="lg" />
              </div>
              <h3 id="rs-confirm-title" className="admin-modal-confirm-title">{state.title}</h3>
              <p id="rs-confirm-message" className="admin-modal-confirm-message">{state.message}</p>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => finish(false)}>
                {state.cancelText}
              </button>
              <button
                type="button"
                className={`admin-btn admin-btn-sm ${state.variant === 'danger' ? 'admin-btn-danger' : 'admin-btn-primary'}`}
                onClick={() => finish(true)}
                autoFocus
              >
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return ctx
}
